import { Box, Stack } from '@mui/material';
import { useEffect } from 'react';
import { Path, useWatch } from 'react-hook-form';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { BeneficiaryPowerOfAppointmentPower, EntityKind } from '@/types/schema';

import { useEntityDetailsContext } from '../contexts/entityDetails/entityDetails.context';
import { POWER_OF_APPOINTMENT_ITEMS } from './EntityBeneficiariesSubform.constants';
import { BeneficiaryFormPowerOfAppointment } from './EntityBeneficiariesSubform.types';

type PowerOfAppointmentFormPaths = Path<BeneficiaryFormPowerOfAppointment>;

interface PowerOfAppointmentFormProps {
  fieldName: 'beneficiaryFormPowerOfAppointment';
}

export function PowerOfAppointmentForm({
  fieldName,
}: PowerOfAppointmentFormProps) {
  const { entityKind } = useEntityDetailsContext();
  const { control, setValue } =
    useFormContext<BeneficiaryFormPowerOfAppointment>();

  const isHasPowerOfAppointmentToggled = useWatch({
    name: `${fieldName}._hasPowerOfAppointment` as '_hasPowerOfAppointment' satisfies PowerOfAppointmentFormPaths,
    control,
  });

  const selectedPower = useWatch({
    name: `${fieldName}.power` as 'power' satisfies PowerOfAppointmentFormPaths,
    control,
  });

  const isLimitedPowerSelected =
    selectedPower === BeneficiaryPowerOfAppointmentPower.Special;

  const isOtherPowerSelected =
    selectedPower === BeneficiaryPowerOfAppointmentPower.Other;

  useEffect(
    function initPower() {
      if (isHasPowerOfAppointmentToggled && !selectedPower) {
        setValue(
          `${fieldName}.power` as 'power' satisfies PowerOfAppointmentFormPaths,
          BeneficiaryPowerOfAppointmentPower.General
        );
      }
    },
    [fieldName, isHasPowerOfAppointmentToggled, selectedPower, setValue]
  );

  if (entityKind && NO_POWER_OF_APPOINTMENT_ENTITY_KINDS.includes(entityKind)) {
    return null;
  }

  return (
    <Card
      variant="filled"
      sx={{
        p: 2,
        borderRadius: '6px',
      }}
    >
      <Stack spacing={1}>
        <FormAwareCheckbox<BeneficiaryFormPowerOfAppointment>
          label="Has power of appointment"
          fieldName={
            `${fieldName}._hasPowerOfAppointment` as '_hasPowerOfAppointment' satisfies PowerOfAppointmentFormPaths
          }
          control={control}
        />
        {isHasPowerOfAppointmentToggled && (
          <Stack pl={7} spacing={1}>
            <Box maxWidth={250}>
              <FormAwareSelectInput<BeneficiaryFormPowerOfAppointment>
                required
                label="Power of appointment"
                hideLabel
                fieldName={
                  `${fieldName}.power` as 'power' satisfies PowerOfAppointmentFormPaths
                }
                control={control}
                options={POWER_OF_APPOINTMENT_ITEMS}
              />
            </Box>
            {(isLimitedPowerSelected || isOtherPowerSelected) && (
              <FormAwareTextInput<BeneficiaryFormPowerOfAppointment>
                label="Notes"
                hideLabel
                placeholder="Specify details"
                multiline
                fieldName={
                  `${fieldName}.powerOtherNote` as 'powerOtherNote' satisfies PowerOfAppointmentFormPaths
                }
                control={control}
                rows={2}
                resizable
              />
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

export const NO_POWER_OF_APPOINTMENT_ENTITY_KINDS: EntityKind[] = [
  EntityKind.IndividualPersonalAccount,
  EntityKind.JointPersonalAccount,
  EntityKind.RetirementPersonalAccount,
  EntityKind.QualifiedTuitionPersonalAccount,
  EntityKind.CustodialPersonalAccount,
  EntityKind.DonorAdvisedFund,
  EntityKind.PrivateFoundation,
];
